import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import {
  QUERY_PAYABLES as QUERY_PAYABLES_NEW,
  QUERY_PAYABLES_SUMMARY
} from "../../../query/payables/keys";
import {
  QUERY_PAYABLE,
  QUERY_PAYABLES as QUERY_PAYABLES_LEGACY
} from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { Payable } from "../../../services/api/payments/payables";

const CANCELLABLE_STATUSES: ReadonlySet<Payable["status"]> = new Set<
  Payable["status"]
>(["Open", "Overdue", "Pending"]);

const invalidateAfterMutation = (successItems: Payable[]) => {
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_NEW);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_LEGACY);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_SUMMARY);
  successItems.forEach(payable => {
    if (payable.payableId) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.payableId]);
    }
  });
};

const processCancelPayables = (
  items: Payable[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<Payable>({
    onProgress,
    action: async item => {
      if (!item.payableId) {
        throw new Error("Payable is missing payableId");
      }
      if (!CANCELLABLE_STATUSES.has(item.status)) {
        throw new Error(
          `Cannot cancel payable with status "${item.status}". Only open, overdue, and pending payables can be cancelled.`
        );
      }
      return paymentsService.payable.update(item.payableId, {
        status: InvoiceStatus.Cancelled
      });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkCancelPayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: Payable[], onSuccess?: () => void) => {
    const count = items.length;
    const pluralized = count > 1 ? "payables" : "payable";

    return runBulkAction({
      actionName: "Cancel payables",
      itemCount: count,
      confirmDescription: `Are you sure you want to cancel ${count.toLocaleString()} ${pluralized}? Cancelled payables will not be paid.`,
      confirmLabel: "Cancel payables",
      confirmDestructive: true,
      prepare: async () => items,
      process: async (data, onProgress) =>
        processCancelPayables(data, onProgress, onSuccess)
    });
  };
};
